<script setup lang="ts">
    import { reactive, onMounted, inject, computed } from 'vue';
    import Tabs from '@shared/components/Tabs.vue';
    import Tab from '@shared/components/Tab.vue';
    import Loading from '@shared/components/Loading.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import RichEditor from '@shared/components/RichEditor/RichEditor.vue';
    import LinkEditor from '@shared/components/RichEditor/LinkEditor.vue';
    import ColorPickerSwatches from '@shared/components/ColorPickers/VACP/ColorPickerSwatches.vue';
    import AiEditor from '@cwd/AiTools/AiEditor.vue';
    import { setupRichEditorProvides } from '@shared/components/RichEditor/setupProvides';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { CurrentAccountReference } from '@shared/Root/Context.access';
    import { get, update, getTimeZones, getDefaultTimeZone, type TimeZone } from './AccountInfo.access';
    import { getCategories, saveCategory, type Category } from './AccountSubscriptionCategories.access';
    import CategoryModal from './CategoryModal.vue';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const hasIgnoreUnsubscribeFeature = computed(() => featureAccess.hasFeature('CategoryIgnoreUnsubscribe'));
    const hasAllowedSendTimesFeature = computed(() => featureAccess.hasFeature('CategoryAllowedSendTimes'));
    const { aiEditor } = setupRichEditorProvides();

    const DAY_BITS = [
        { bit: 2, label: () => $Resources.CronWeekdayMON },
        { bit: 4, label: () => $Resources.CronWeekdayTUE },
        { bit: 8, label: () => $Resources.CronWeekdayWED },
        { bit: 16, label: () => $Resources.CronWeekdayTHU },
        { bit: 32, label: () => $Resources.CronWeekdayFRI },
        { bit: 64, label: () => $Resources.CronWeekdaySAT },
        { bit: 1, label: () => $Resources.CronWeekdaySUN },
    ];

    const state = reactive<{
        isLoading: boolean;
        account: CurrentAccountReference | null;
        categories: Category[];
        categoriesLoading: boolean;
        timezones: TimeZone[];
        defaultTimezone: string;
        modal: {
            show: boolean;
            editing: Category | null;
        };
        pages: {
            mailSent: string;
            successful: string;
            failed: string;
        };
    }>({
        isLoading: true,
        account: null,
        categories: [],
        categoriesLoading: false,
        timezones: [],
        defaultTimezone: '',
        modal: { show: false, editing: null },
        pages: { mailSent: '', successful: '', failed: '' },
    });

    onMounted(async () => {
        const [account, timezones, defaultTimezone] = await Promise.all([get(), getTimeZones(), getDefaultTimeZone(), loadCategories()]);

        state.account = account;
        state.timezones = timezones;
        state.defaultTimezone = defaultTimezone;

        state.pages.mailSent = account.SubscriptionMailSentText || '';
        state.pages.successful = account.SubscriptionSuccessfulText || '';
        state.pages.failed = account.SubscriptionFailedText || '';

        state.isLoading = false;
    });

    async function loadCategories(): Promise<void> {
        state.categoriesLoading = true;
        state.categories = await getCategories();
        state.categoriesLoading = false;
    }

    function formatAllowedDays(mask: number | null): string {
        if (!mask) return '';
        return DAY_BITS.filter((d) => mask & d.bit)
            .map((d) => d.label())
            .join(', ');
    }

    function formatTime(time: string | null): string {
        return time ? time.substring(0, 5) : '';
    }

    function getTimezoneName(timezoneId: string | null): string {
        if (!timezoneId) return '';
        const timezone = state.timezones.find((tz) => tz.TimeZoneId === timezoneId);
        return timezone ? timezone.Name : timezoneId;
    }

    function onShowOnlyToRecipientsChange(newVal: any): void {
        if (!state.account) return;

        const value = !!newVal;
        state.account.ShowSubscriptionsOnlyToRecipients = value;
        saveAccountProperty('ShowSubscriptionsOnlyToRecipients', value);
    }

    function propertyLabel(key: string): string {
        const resources = $Resources as unknown as Record<string, string>;
        return resources['PropertyAccount' + key + 'Label'] || resources['propertyAccount' + key + 'Label'] || key;
    }

    async function saveAccountProperty(key: string, value: any): Promise<void> {
        try {
            await update(key, value);
            bsAlert({ type: AlertTypes.Success, title: $Resources.PropertyUpdatedLabel, content: propertyLabel(key) });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.PropertyNotUpdatedLabel, content: propertyLabel(key) });
        }
    }

    function openCreateCategory(): void {
        state.modal.editing = null;
        state.modal.show = true;
    }

    function openEditCategory(category: Category): void {
        state.modal.editing = category;
        state.modal.show = true;
    }

    function closeModal(): void {
        state.modal.show = false;
    }

    function onCategorySaved(category: Category, wasEditing: boolean): void {
        if (wasEditing) {
            const index = state.categories.findIndex((c) => c.CategoryId === category.CategoryId);
            if (index !== -1) state.categories.splice(index, 1, category);
        } else {
            state.categories.push(category);
        }
    }

    async function onCategoryFieldChange(category: Category): Promise<void> {
        try {
            const result = await saveCategory(category);
            const index = state.categories.findIndex((c) => c.CategoryId === category.CategoryId);
            if (index !== -1) state.categories.splice(index, 1, result);
            bsAlert({ type: AlertTypes.Success, title: $Resources.AlertSuccessHeading, content: $Resources.AlertCategoryUpdated });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorCreateHeading, content: $Resources.ErrorCouldNotUpdateCategory });
        }
    }

    const categoryFieldFocusValues = new Map<string, string | null>();

    function onCategoryFieldFocus(category: Category, field: 'Name' | 'Description'): void {
        categoryFieldFocusValues.set(`${category.CategoryId}.${field}`, category[field]);
    }

    function onCategoryFieldBlur(category: Category, field: 'Name' | 'Description'): void {
        const key = `${category.CategoryId}.${field}`;
        if (categoryFieldFocusValues.get(key) === category[field]) return;
        categoryFieldFocusValues.delete(key);
        onCategoryFieldChange(category);
    }

    function onNameInput(category: Category, newVal: string): void {
        category.Name = newVal;
    }

    function onDescriptionInput(category: Category, newVal: string): void {
        category.Description = newVal;
    }

    function onIsPublicChange(category: Category, newVal: any): void {
        category.IsPublic = !!newVal;
        onCategoryFieldChange(category);
    }

    function onIgnoreUnsubscribeChange(category: Category, newVal: any): void {
        category.IgnoreUnsubscribe = !!newVal;
        onCategoryFieldChange(category);
    }

    type PageKey = 'mailSent' | 'successful' | 'failed';

    const pageProperty: Record<PageKey, string> = {
        mailSent: 'SubscriptionMailSentText',
        successful: 'SubscriptionSuccessfulText',
        failed: 'SubscriptionFailedText',
    };

    const pagePathSegment: Record<PageKey, string> = {
        mailSent: 'OptInEmailSent',
        successful: 'Ok',
        failed: 'Failed',
    };

    function previewUrl(page: PageKey): string {
        if (!state.account) return '';
        return combineUrlSegments(state.account.PublicEventUrl.PublicHost, 'Subscriptions', state.account.AccountId, pagePathSegment[page]);
    }

    function savedPageText(page: PageKey): string {
        return ((state.account as any)?.[pageProperty[page]] as string) || '';
    }

    function resetPageText(page: PageKey): void {
        state.pages[page] = '';
        saveAccountProperty(pageProperty[page], '').then(() => {
            if (state.account) (state.account as any)[pageProperty[page]] = '';
        });
    }

    async function savePageText(page: PageKey): Promise<void> {
        await saveAccountProperty(pageProperty[page], state.pages[page]);
        if (state.account) (state.account as any)[pageProperty[page]] = state.pages[page];
    }
</script>

<template>
    <Loading :show="state.isLoading || state.categoriesLoading" loading-style="progress-bar" fixed></Loading>

    <div v-if="state.account">
        <h1 class="h2 m-lg-b">{{ $Resources.AccountPageTabsAccountSubscriptions }}</h1>
        <p>{{ $Resources.AccountSubscriptionsInfoText }}</p>

        <Tabs>
            <Tab :title="$Resources.AccountPageTabsAccountSubscriptions">
                <div class="max-700">
                    <h2 class="h3">{{ $Resources.AccountSubscriptionsHeading }}</h2>

                    <div class="form-group">
                        <FormCheckable
                            :label="$Resources.AccountSubscriptionsShowOnlyReceivedSubscriptionsLabel"
                            :switchStyle="SwitchStyle.Standard"
                            :checked="state.account.ShowSubscriptionsOnlyToRecipients"
                            @update:modelValue="onShowOnlyToRecipientsChange">
                            <template #label>
                                <span
                                    v-html="
                                        $Resources.AccountSubscriptionsShowOnlyReceivedSubscriptionsLabel.replace(
                                            '{{PreviewPreferenceCenterUrl}}',
                                            state.account?.PreviewPreferenceCenterUrl
                                        )
                                    "></span>
                            </template>
                        </FormCheckable>
                    </div>

                    <div class="form-group">
                        <button type="button" class="btn btn-primary" @click="openCreateCategory">{{ $Resources.NewCategoryButton }}</button>
                    </div>

                    <fieldset class="fieldset" v-for="category in state.categories" :key="category.CategoryId">
                        <FormInput
                            class="form-group"
                            :label="$Resources.AccountSubscriptionsName"
                            :modelValue="category.Name"
                            :maxlength="255"
                            @update:modelValue="onNameInput(category, $event)"
                            @onFocus="onCategoryFieldFocus(category, 'Name')"
                            @onBlur="onCategoryFieldBlur(category, 'Name')"></FormInput>

                        <FormInput
                            class="form-group"
                            :label="$Resources.AccountSubscriptionsDescription"
                            :modelValue="category.Description"
                            @update:modelValue="onDescriptionInput(category, $event)"
                            @onFocus="onCategoryFieldFocus(category, 'Description')"
                            @onBlur="onCategoryFieldBlur(category, 'Description')"></FormInput>

                        <div class="d-flex flex-wrap col-gap-lg m-xs-b">
                            <FormCheckable
                                :label="$Resources.AccountSubscriptionTogglePublicLabel"
                                :switchStyle="SwitchStyle.Standard"
                                :checked="category.IsPublic"
                                @update:modelValue="onIsPublicChange(category, $event)"></FormCheckable>

                            <FormCheckable
                                v-if="hasIgnoreUnsubscribeFeature"
                                :label="$Resources.CategoryIgnoreUnsubscribeLabel"
                                :switchStyle="SwitchStyle.Standard"
                                :checked="category.IgnoreUnsubscribe"
                                @update:modelValue="onIgnoreUnsubscribeChange(category, $event)"></FormCheckable>
                        </div>

                        <div
                            v-if="
                                (category.AllowedDays && category.AllowedFromTime && category.AllowedToTime && category.AllowedTimezone) ||
                                hasAllowedSendTimesFeature
                            ">
                            <p class="m-md-b">
                                <strong>{{ $Resources.AllowedSendTimesHeading }}:</strong>

                                <span class="m-xs-l" v-if="category.AllowedFromTime">
                                    {{ formatAllowedDays(category.AllowedDays) }} {{ formatTime(category.AllowedFromTime) }}&#x2013;{{
                                        formatTime(category.AllowedToTime)
                                    }}
                                </span>
                                <span class="m-xs-l" v-else>[{{ $Resources.ValueIsMissing }}]</span>

                                <a href="javascript:void(0)" role="button" class="m-xs-l strong" @click="openEditCategory(category)">
                                    {{ $Resources.EditButton }}
                                </a>

                                <template v-if="category.AllowedFromTime">
                                    <br />
                                    <small>{{ getTimezoneName(category.AllowedTimezone) }}</small>
                                </template>
                            </p>
                        </div>
                    </fieldset>
                </div>
            </Tab>

            <Tab :title="$Resources.AccountPageTabsSubscriptionPages">
                <div class="max-700">
                    <h2 class="h3">{{ $Resources.AccountPageTabsSubscriptionPages }}</h2>

                    <p class="m-lg-b">{{ $Resources.AccountSubscriptionTextDescription }}</p>

                    <div class="m-lg-b">
                        <h4>{{ $Resources.AccountSubscriptionEmailSentTextLabel }}</h4>

                        <RichEditor class="m-xs-b" :modelValue="state.pages.mailSent" @update:modelValue="state.pages.mailSent = $event"></RichEditor>

                        <div class="d-flex flex-wrap gap-xs m-xs-t">
                            <a class="btn btn-sm btn-default m-auto-r" :href="previewUrl('mailSent')" target="_blank" rel="noopener">
                                {{ $Resources.PreviewButton }}
                                <span class="glyphicon glyphicon-new-window c-light m-xxs-l" aria-hidden="true"></span>
                            </a>

                            <button type="button" class="btn btn-sm btn-default" :disabled="!savedPageText('mailSent')" @click="resetPageText('mailSent')">
                                {{ $Resources.SubscriptionPagesResetBtn }}
                            </button>

                            <button
                                type="button"
                                class="btn btn-sm btn-primary"
                                :disabled="savedPageText('mailSent') === state.pages.mailSent"
                                @click="savePageText('mailSent')">
                                {{ $Resources.SaveBtn }}
                            </button>
                        </div>
                    </div>

                    <div class="m-lg-b">
                        <h4>{{ $Resources.AccountSubscriptionSuccessfulTextLabel }}</h4>

                        <RichEditor class="m-xs-b" :modelValue="state.pages.successful" @update:modelValue="state.pages.successful = $event"></RichEditor>

                        <div class="d-flex flex-wrap gap-xs m-xs-t">
                            <a class="btn btn-sm btn-default m-auto-r" :href="previewUrl('successful')" target="_blank" rel="noopener">
                                {{ $Resources.PreviewButton }}
                                <span class="glyphicon glyphicon-new-window c-light m-xxs-l" aria-hidden="true"></span>
                            </a>

                            <button type="button" class="btn btn-sm btn-default" :disabled="!savedPageText('successful')" @click="resetPageText('successful')">
                                {{ $Resources.SubscriptionPagesResetBtn }}
                            </button>

                            <button
                                type="button"
                                class="btn btn-sm btn-primary"
                                :disabled="savedPageText('successful') === state.pages.successful"
                                @click="savePageText('successful')">
                                {{ $Resources.SaveBtn }}
                            </button>
                        </div>
                    </div>

                    <div class="m-lg-b">
                        <h4>{{ $Resources.AccountSubscriptionFailedTextLabel }}</h4>

                        <RichEditor class="m-xs-b" :modelValue="state.pages.failed" @update:modelValue="state.pages.failed = $event"></RichEditor>

                        <div class="d-flex flex-wrap gap-xs m-xs-t">
                            <a class="btn btn-sm btn-default m-auto-r" :href="previewUrl('failed')" target="_blank" rel="noopener">
                                {{ $Resources.PreviewButton }}
                                <span class="glyphicon glyphicon-new-window c-light m-xxs-l" aria-hidden="true"></span>
                            </a>

                            <button type="button" class="btn btn-sm btn-default" :disabled="!savedPageText('failed')" @click="resetPageText('failed')">
                                {{ $Resources.SubscriptionPagesResetBtn }}
                            </button>

                            <button
                                type="button"
                                class="btn btn-sm btn-primary"
                                :disabled="savedPageText('failed') === state.pages.failed"
                                @click="savePageText('failed')">
                                {{ $Resources.SaveBtn }}
                            </button>
                        </div>
                    </div>
                </div>
            </Tab>
        </Tabs>

        <CategoryModal
            :show="state.modal.show"
            :category="state.modal.editing"
            :timezones="state.timezones"
            :defaultTimezone="state.defaultTimezone"
            :hasAllowedSendTimesFeature="hasAllowedSendTimesFeature"
            @close="closeModal"
            @saved="onCategorySaved"></CategoryModal>

        <LinkEditor></LinkEditor>
        <ColorPickerSwatches></ColorPickerSwatches>
        <AiEditor
            v-if="aiEditor"
            :getContent="aiEditor.getState.value.getContent"
            :show="aiEditor.getState.value.show"
            @update:content="(newValue: string) => aiEditor!.getState.value.updateContent(newValue)"
            @close="aiEditor.closeEditor"></AiEditor>
    </div>
</template>
